import React, { useState } from 'react';
import {
  ChevronDown, ChevronRight, Database, Utensils, Brain, Shield, Zap, Heart,
  HelpCircle, LogIn, Home, BookOpen, Sparkles, Globe, Camera, MessageSquare,
  Image, Search, Users, ShoppingCart, CreditCard, Server, Bell, RefreshCw,
  Bug, Lock, ChefHat, Layers, FileText, Video, ArrowRight,
} from 'lucide-react';

// ============================================================================
// Shared Components
// ============================================================================

const Section = ({ title, icon: Icon, children, defaultOpen = false, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
        {Icon && <Icon className="w-5 h-5 text-orange-500 shrink-0" />}
        <span className="font-semibold text-gray-900 dark:text-white">{title}</span>
        {badge && (
          <span className="ml-auto text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div className="p-6 space-y-5 bg-white dark:bg-gray-900">
          {children}
        </div>
      )}
    </div>
  );
};

const PhaseHeader = ({ number, title }) => (
  <div className="flex items-center gap-3 mt-8 mb-4 first:mt-0">
    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white text-sm font-bold shrink-0">
      {number}
    </div>
    <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
  </div>
);

const SubHeading = ({ children }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 mt-1">
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{children}</p>
);

const Ol = ({ children }) => (
  <ol className="list-decimal list-outside ml-5 space-y-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
    {children}
  </ol>
);

const Ul = ({ children }) => (
  <ul className="list-disc list-outside ml-5 space-y-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
    {children}
  </ul>
);

const Li = ({ children }) => <li>{children}</li>;

const Mono = ({ children }) => (
  <code className="text-xs bg-gray-100 dark:bg-gray-800 text-orange-600 dark:text-orange-400 px-1.5 py-0.5 rounded font-mono">
    {children}
  </code>
);

const Code = ({ children }) => (
  <pre className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs leading-relaxed text-gray-700 dark:text-gray-300 font-mono">
    {children}
  </pre>
);

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 dark:bg-gray-800">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
            {row.map((cell, j) => (
              <td key={j} className={`py-2.5 px-4 text-gray-600 dark:text-gray-300 ${j === 0 ? 'font-mono text-xs text-orange-600 dark:text-orange-400 whitespace-nowrap' : ''}`}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Placeholder = () => (
  <P><em className="text-gray-400 dark:text-gray-500">To be documented — audit pending.</em></P>
);

// ============================================================================
// Page
// ============================================================================

const DocsPage = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">System Documentation</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          End-to-end architecture for every feature in Recipe Genie. Organized by user journey: App → API → Worker.
        </p>
      </div>

      {/* ================================================================ */}
      {/* PHASE 1: AUTH & ONBOARDING                                       */}
      {/* ================================================================ */}
      <PhaseHeader number={1} title="Auth & Onboarding" />

      <Section title="Onboarding Screens" icon={Sparkles}>
        <Placeholder />
      </Section>

      <Section title="Sign Up (Create Account)" icon={LogIn}>
        <Placeholder />
      </Section>

      <Section title="Sign In & Token Refresh" icon={Lock}>
        <Placeholder />
      </Section>

      <Section title="Forgot Password / Reset" icon={RefreshCw}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 2: HOME & NAVIGATION                                       */}
      {/* ================================================================ */}
      <PhaseHeader number={2} title="Home & Navigation" />

      <Section title="Home Screen" icon={Home}>
        <Placeholder />
      </Section>

      <Section title="Bottom Nav & Routing" icon={Layers}>
        <Placeholder />
      </Section>

      <Section title="Settings Screen" icon={FileText}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 3: RECIPES CORE                                            */}
      {/* ================================================================ */}
      <PhaseHeader number={3} title="Recipes Core" />

      <Section title="Recipe List (Fetch, Filter, Search)" icon={BookOpen}>
        <Placeholder />
      </Section>

      <Section title="Recipe Detail Screen" icon={ChefHat}>
        <Placeholder />
      </Section>

      <Section title="Create Recipe (Manual Form)" icon={Zap}>
        <Placeholder />
      </Section>

      <Section title="Edit & Delete Recipe" icon={BookOpen}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 4: AI RECIPE CREATION                                      */}
      {/* ================================================================ */}
      <PhaseHeader number={4} title="AI Recipe Creation" />

      <Section title="Web Import — All Platform Routes" icon={Globe} badge="Documented">
        <div>
          <SubHeading>Entry Points</SubHeading>
          <Table
            headers={['Trigger', 'Flutter path']}
            rows={[
              ['User types / pastes URL', '_importRecipe() → _resolveRedirects() → _startUrlScrape()'],
              ['Android share intent', 'receive_sharing_intent → same pipeline'],
            ]}
          />
          <div className="mt-3">
            <P>Short-link resolution fires first for: <Mono>youtu.be</Mono> <Mono>vm.tiktok.com</Mono> <Mono>vt.tiktok.com</Mono> <Mono>fb.watch</Mono> <Mono>t.co</Mono> <Mono>bit.ly</Mono> <Mono>tinyurl.com</Mono> <Mono>ow.ly</Mono> <Mono>is.gd</Mono></P>
          </div>
        </div>

        <div>
          <SubHeading>All Paths at a Glance</SubHeading>
          <Table
            headers={['Platform', 'Flutter', 'Worker endpoint', 'Extraction method']}
            rows={[
              ['TikTok', 'Skip WebView → scrapeRecipeFromUrl', 'scrape_recipe_url', 'TikTok oEmbed API'],
              ['Facebook post', 'Skip WebView → scrapeRecipeFromUrl', 'scrape_recipe_url', 'Meta tags + embedded JSON'],
              ['Facebook video/reel', 'Skip WebView → scrapeRecipeFromUrl', 'extract_recipe_from_video', 'yt-dlp subtitles → description fallback'],
              ['YouTube', 'WebView JS caption extraction → parseRecipeTranscript', 'parse_recipe_transcript', 'Client-side caption XML fetch'],
              ['Instagram', 'WebView → parseRecipeHtml or og:description fallback', 'parse_recipe_html / parse_recipe_transcript', 'HTML scrape or og:description'],
              ['Recipe websites', 'WebView → parseRecipeHtml', 'parse_recipe_html', 'JSON-LD schema.org/Recipe → HTML → AI'],
              ['X / Threads', 'WebView → og:description if available', 'parse_recipe_transcript', 'og:description only; paste-text last resort'],
            ]}
          />
        </div>

        <div>
          <SubHeading>Path A — TikTok</SubHeading>
          <P><Mono>tiktok.com</Mono> · <Mono>vm.tiktok.com</Mono> · <Mono>vt.tiktok.com</Mono></P>
          <Code>{`Flutter: skip WebView → POST /scrape-recipe-url
Worker: scrape_recipe_url
  → is_tiktok → return early (no HTML fetch, no browser-worker)
  → _parse_html_to_recipe(html='')
  → extract_tiktok_content() → TikTok oEmbed API
      returns: caption (title), author, thumbnail_url
  → Claude extracts recipe from caption
  → thumbnail_url → source_image_url (AI image generation skipped)`}</Code>
        </div>

        <div>
          <SubHeading>Path B — Facebook Post</SubHeading>
          <P><Mono>facebook.com/share/r/</Mono> · <Mono>facebook.com/&lt;user&gt;/posts/</Mono> · page posts</P>
          <Code>{`Flutter: skip WebView → POST /scrape-recipe-url
Worker: scrape_recipe_url
  → curl fetch HTML
  → _parse_html_to_recipe(html)
  → best_facebook_post_content():
      tries meta tags (og:description) + embedded JSON
  → Claude extracts recipe from caption text
  → if caption too short + post has a photo:
      vision AI fallback — downloads photo, runs image extraction`}</Code>
        </div>

        <div>
          <SubHeading>Path C — Facebook Video / Reel</SubHeading>
          <P><Mono>facebook.com/share/v/</Mono> · <Mono>facebook.com/reel/</Mono> · <Mono>facebook.com/&lt;user&gt;/videos/</Mono></P>
          <Code>{`Flutter: skip WebView → POST /scrape-recipe-url
API router: detects FB video URL pattern → routes to extract_recipe_from_video
Worker: extract_recipe_from_video (yt-dlp)
  → yt-dlp fetches subtitles
  → if subtitles found:
      Claude extracts recipe from subtitle text
  → if no subtitles:
      Claude extracts from video description
      steps_in_source flag enforced in Python:
        if false → instructions = [] regardless of AI output
  → if 0 steps returned:
      Flutter shows "Generate Steps?" dialog → user opt-in
      → POST /generate-recipe-steps → Claude generates steps`}</Code>
        </div>

        <div>
          <SubHeading>Path D — YouTube</SubHeading>
          <P><Mono>youtube.com</Mono> · <Mono>youtu.be</Mono> · <Mono>youtube.com/shorts/</Mono></P>
          <Code>{`Flutter: WebView loads YouTube page
  → JS injects ytInitialPlayerResponse (pre-registered before page load)
  → fetches caption XML via YtCaptionFetch JS channel
  → picks best content: transcript > description (≥200 chars)
  → if transcript or long description found:
      → POST /parse-recipe-transcript
      Worker: parse_recipe_transcript → Claude extracts recipe
  → if nothing substantial:
      throw _NoVisibleRecipeException → "paste text" prompt

Note: no server-side yt-dlp for YouTube — captions pulled client-side only`}</Code>
        </div>

        <div>
          <SubHeading>Path E — Instagram</SubHeading>
          <P><Mono>instagram.com/p/</Mono> · <Mono>instagram.com/reel/</Mono></P>
          <Code>{`Flutter: WebView loads page
  → if HTML ≥5KB:
      → POST /parse-recipe-html (full HTML)
      Worker: _parse_html_to_recipe → JSON-LD / BeautifulSoup / Claude
  → if thin HTML + og:description ≥300 chars (login wall):
      → POST /parse-recipe-transcript (og:description as transcript)
  → if neither:
      server fallback → POST /scrape-recipe-url
      Worker: curl fetch → public posts return og:description in plain HTTP`}</Code>
        </div>

        <div>
          <SubHeading>Path F — Recipe Websites</SubHeading>
          <P>allrecipes.com, bbcgoodfood.com, smittenkitchen.com, and any standard recipe site</P>
          <Code>{`Flutter: WebView loads page
  → HTML ≥5KB → POST /parse-recipe-html
Worker: parse_recipe_html → _parse_html_to_recipe
  → JSON-LD (schema.org/Recipe) → preferred, used directly
  → BeautifulSoup HTML parse if no JSON-LD
  → Claude extracts structured recipe JSON
  → og:image → source_image_url (AI image generation skipped)

WebView failure fallback:
  → server curl fetch → same worker path`}</Code>
        </div>

        <div>
          <SubHeading>Path G — X / Threads / Other Gated Platforms</SubHeading>
          <Code>{`Flutter: WebView loads page
  → thin HTML likely (login wall / JS-only)
  → if og:description ≥300 chars:
      → POST /parse-recipe-transcript (og:description)
  → else:
      server fallback → curl → usually also thin
      → "paste text" prompt as last resort`}</Code>
        </div>

        <div>
          <SubHeading>After Every Import (All Paths)</SubHeading>
          <Ul>
            <Li><strong>0 steps + ingredients present</strong> → Flutter shows "Generate Steps?" dialog → user opt-in → <Mono>POST /generate-recipe-steps</Mono> (Claude, file_processing queue)</Li>
            <Li><strong>source_image_url present</strong> (og:image, TikTok thumbnail, JSON-LD image) → AI image generation skipped</Li>
            <Li><strong>No source image</strong> → Flutter offers AI image generation (quota-exempt for web imports)</Li>
            <Li><strong>Community dedup</strong> → if URL already imported publicly, user is prompted before saving a duplicate</Li>
            <Li><strong>Redis cache</strong> → successful extractions cached by canonical URL; <Mono>force_refresh=true</Mono> bypasses</Li>
          </Ul>
        </div>
      </Section>

      <Section title="Video Import Deep Dive (yt-dlp + YouTube captions)" icon={Video} badge="Documented">
        <div>
          <SubHeading>YouTube — Client-Side Caption Extraction</SubHeading>
          <P>YouTube is handled entirely in the Flutter WebView — no server-side yt-dlp. This avoids datacenter IP rate-limiting and keeps caption extraction close to the user's session.</P>
          <Code>{`1. WebView loads youtube.com page
2. JS channels registered BEFORE page load (Android requirement):
     YtExtractor   → receives ytInitialPlayerResponse JSON
     YtCaptionFetch → receives raw caption XML
3. After pageFinished, JS reads ytInitialPlayerResponse from window
4. Extracts captionTracks[], picks English track (or first available)
5. Fetches caption XML via fetch() in the WebView context
6. Sends XML text through YtCaptionFetch channel to Flutter
7. Flutter parses XML → plain transcript text
8. Sends to POST /parse-recipe-transcript with:
     transcript, videoTitle, videoAuthor, videoDescription
9. Worker: Claude extracts structured recipe from transcript`}</Code>
          <div className="mt-3">
            <P>Content priority: <strong>transcript</strong> → <strong>description ≥200 chars</strong> → fail with paste-text prompt (title alone is not enough — Claude hallucinated recipes from titles like "Orange Chicken at Home").</P>
          </div>
        </div>

        <div>
          <SubHeading>Facebook Video / Reel — yt-dlp Server-Side</SubHeading>
          <P>Facebook video URLs are detected by the API router before dispatching to the worker.</P>
          <Table
            headers={['URL pattern', 'Routed to']}
            rows={[
              ['facebook.com/share/v/', 'extract_recipe_from_video (yt-dlp)'],
              ['facebook.com/reel/', 'extract_recipe_from_video (yt-dlp)'],
              ['facebook.com/<user>/videos/', 'extract_recipe_from_video (yt-dlp)'],
              ['facebook.com/share/r/', 'scrape_recipe_url (caption scrape)'],
              ['facebook.com/<user>/posts/', 'scrape_recipe_url (caption scrape)'],
            ]}
          />
          <div className="mt-3">
            <P><strong>steps_in_source enforcement:</strong> Claude self-reports a <Mono>steps_in_source: true/false</Mono> boolean. Python clears <Mono>instructions = []</Mono> if false, regardless of what Claude returned. Prevents silent AI step invention when only description text is available.</P>
          </div>
        </div>

        <div>
          <SubHeading>TikTok — oEmbed (No Browser, No yt-dlp)</SubHeading>
          <P>TikTok is the lightest path — no WebView, no Playwright browser-worker, no yt-dlp subprocess.</P>
          <Code>{`POST /scrape-recipe-url
  → scrape_recipe_url task
  → is_tiktok check → early return before any HTTP fetch
  → _parse_html_to_recipe(html='')
  → extract_tiktok_content():
      1. HEAD request resolves vt.tiktok.com / vm.tiktok.com short URLs
      2. Regex extracts @username + video_id from full URL
      3. GET https://www.tiktok.com/oembed?url=...
      4. Returns: title (caption), author_name, thumbnail_url
  → Claude extracts recipe from caption
  → thumbnail_url set as source_image_url

Total time: ~2s (was ~22s when going through browser-worker)`}</Code>
        </div>

        <div>
          <SubHeading>Worker Queues for Video Tasks</SubHeading>
          <Table
            headers={['Task', 'Queue', 'Worker mode']}
            rows={[
              ['scrape_recipe_url', 'file_processing', 'recipe / all'],
              ['extract_recipe_from_video', 'file_processing', 'recipe / all'],
              ['parse_recipe_transcript', 'file_processing', 'recipe / all'],
              ['parse_recipe_html', 'file_processing', 'recipe / all'],
              ['generate_recipe_steps', 'file_processing', 'recipe / all'],
            ]}
          />
        </div>
      </Section>

      <Section title="Photo Import (Camera → Vision → Recipe)" icon={Camera}>
        <Placeholder />
      </Section>

      <Section title="Book / Scan Import (PDF → Recipes)" icon={BookOpen}>
        <Placeholder />
      </Section>

      <Section title="Genie Chat (Conversation → Generate → Save)" icon={MessageSquare} badge="Documented">
        <div>
          <SubHeading>Architecture</SubHeading>
          <Code>{`User message -> Claude Haiku -> picks tools -> execute -> reasons -> responds
                   (max 5 iterations per turn)`}</Code>
          <div className="mt-3">
            <P>The Genie uses Claude's native <Mono>tool_use</Mono> feature. Claude decides which tools to call based on the user's message. Feature flag: <Mono>GENIE_AGENTIC_MODE=true</Mono>.</P>
          </div>
        </div>

        <div>
          <SubHeading>14 Tools Available</SubHeading>
          <Table
            headers={['Tool', 'Purpose']}
            rows={[
              ['search_my_recipes', "Search user's saved recipes by keyword/cuisine/category"],
              ['get_recipe_details', 'Get full recipe by ID'],
              ['search_community_recipes', 'Browse public recipes with sort/filter'],
              ['find_similar_recipes', 'Semantic search via pgvector embeddings'],
              ['get_ingredient_info', 'Nutritional info, substitutes (DB + AI fallback)'],
              ['get_cuisine_info', 'Cuisine details from DB'],
              ['get_user_preferences', 'Dietary prefs, tier, recipe count'],
              ['get_help_article', '15 static help articles (features, subscription, troubleshooting)'],
              ['generate_recipe *', 'Create recipe (only when user taps Generate button)'],
              ['modify_recipe *', 'Modify existing recipe (only on Generate button)'],
              ['calculate_nutrition', 'USDA-based nutrition: reads stored data for saved recipes, cached per-100g for ad-hoc'],
              ['get_pantry', "User's pantry items, expiry dates, running low"],
              ['get_shopping_lists', "User's shopping lists and items"],
              ['add_to_shopping_list', 'Add items to shopping list'],
            ]}
          />
          <div className="mt-3">
            <P>* <Mono>generate_recipe</Mono> and <Mono>modify_recipe</Mono> are only available when the user explicitly taps "Generate now!" — Claude cannot call them on its own.</P>
          </div>
        </div>

        <div>
          <SubHeading>Help Hub</SubHeading>
          <P>15 static articles in <Mono>genie_help_content.py</Mono> covering: app features (overview, photo recipes, URL import, scanning, community, shopping, modifications, AI images, dietary preferences), subscription (tiers, limits, upgrade), and troubleshooting (login, slow responses).</P>
        </div>
      </Section>

      <Section title="Recipe Creation Paths (Summary)" icon={Zap} badge="Documented">
        <Table
          headers={['Path', 'Source', 'How Ingredients Arrive']}
          rows={[
            ['Manual', 'User types in form', 'Ad-hoc names, optional autocomplete link to food_ingredients'],
            ['AI Generation', 'Genie chat / "Create Recipe"', 'AI generates ingredient list as JSON, saved as ad-hoc names'],
            ['URL Import', 'Paste recipe URL', 'Scraped from website HTML, parsed by AI into structured JSON'],
            ['Video Import', 'YouTube/TikTok URL', 'Transcript extracted, AI generates recipe from transcript'],
            ['Photo Import', 'Camera / gallery / scan', 'Claude Vision identifies ingredients, AI builds recipe'],
            ['Community Fork', 'Save from community feed', 'Cloned from source recipe (including dietary_flags)'],
          ]}
        />
        <P>All paths converge at <Mono>POST /api/food/recipes</Mono> which saves the recipe and triggers the 3 async tasks (embedding, dietary flags, nutrition).</P>
      </Section>

      {/* ================================================================ */}
      {/* PHASE 5: AI FEATURES                                             */}
      {/* ================================================================ */}
      <PhaseHeader number={5} title="AI Features & Processing" />

      <Section title="Recipe Image Generation" icon={Image}>
        <Placeholder />
      </Section>

      <Section title="USDA Nutrition Calculation" icon={Heart} badge="Documented">
        <div>
          <SubHeading>Flow</SubHeading>
          <Ol>
            <Li>Recipe saved with ingredients (quantity + unit + name)</Li>
            <Li><Mono>auto_calculate_recipe_nutrition</Mono> fires (skips if calories already set)</Li>
            <Li>For each ingredient: check food_ingredients for cached USDA data</Li>
            <Li>Uncached ingredients: search USDA FoodData Central API</Li>
            <Li>AI (Claude) picks best USDA match from candidates and estimates grams</Li>
            <Li>Nutrition = (USDA nutrients per 100g) × (estimated grams / 100)</Li>
            <Li>Sum all ingredients, divide by servings = per-serving values</Li>
            <Li>Save to food_recipes: calories, protein_g, carbs_g, fat_g, fiber_g, sugar_g</Li>
          </Ol>
        </div>

        <div>
          <SubHeading>USDA API Details</SubHeading>
          <Table
            headers={['Endpoint', 'Purpose']}
            rows={[
              ['/fdc/v1/foods/search', 'Search by ingredient name (Foundation + SR Legacy data types)'],
              ['/fdc/v1/foods', 'Bulk fetch nutrients for matched fdc_ids'],
            ]}
          />
          <div className="mt-3">
            <P>6 nutrients tracked by USDA nutrient ID: calories (1008), protein (1003), carbs (1005), fat (1004), fiber (1079), sugar (2000).</P>
          </div>
        </div>

        <div>
          <SubHeading>AIPedia Ingredient Lookup</SubHeading>
          <P>When a user views an ingredient in AIPedia, <Mono>generate_ingredient_info</Mono> now calls USDA after the AI step. Writes authoritative per-100g values to both the flat columns and <Mono>nutrition_info</Mono> JSONB. This also warms the cache for future recipe calculations.</P>
        </div>

        <div>
          <SubHeading>Genie Chat Nutrition</SubHeading>
          <P>The <Mono>calculate_nutrition</Mono> genie tool uses stored USDA data — no AI guessing:</P>
          <Table
            headers={['Scenario', 'What Happens']}
            rows={[
              ['Saved recipe, nutrition stored', 'Read 6 columns from food_recipes. Instant, consistent.'],
              ['Saved recipe, no nutrition yet', 'Delegates to calculate_recipe_nutrition (real USDA pipeline, auto-saves).'],
              ['Ad-hoc ingredients', 'Exact match against food_ingredients cached per-100g data. Claude does the gram math.'],
            ]}
          />
        </div>

        <div>
          <SubHeading>Backfill</SubHeading>
          <P><Mono>backfill_recipe_nutrition</Mono> finds all recipes with NULL calories that have ingredients, dispatches <Mono>auto_calculate_recipe_nutrition</Mono> for each with a 2-second stagger to respect USDA rate limits.</P>
        </div>
      </Section>

      <Section title="Dietary Flag System" icon={Shield} badge="Documented">
        <div>
          <SubHeading>Three Flag Classes</SubHeading>
          <Table
            headers={['Class', 'Flags', 'Behavior']}
            rows={[
              ['Identity', 'vegan, vegetarian', 'Hard-exclude incompatible content everywhere'],
              ['Allergy', 'gluten_free, dairy_free, nut_free', 'Show all content; warning badge on potential allergens'],
              ['Lifestyle', 'low_carb, keto', 'Badge compatible items; no filtering'],
            ]}
          />
          <div className="mt-3">
            <P><strong>Source of truth:</strong> <Mono>api-backend/utils/dietary_flags.py</Mono> defines DIETARY_FLAGS, IDENTITY_FLAGS, ALLERGY_FLAGS, LIFESTYLE_FLAGS, and shared helpers.</P>
          </div>
        </div>

        <div>
          <SubHeading>How Recipes Get Flags</SubHeading>
          <Ol>
            <Li>AI generates recipe with tags (e.g., "vegan", "gluten-free")</Li>
            <Li><Mono>derive_recipe_dietary_flags</Mono> maps tags to flags</Li>
            <Li>Flags are cross-checked against ingredient booleans</Li>
            <Li>Result stored in <Mono>food_recipes.dietary_flags</Mono> (TEXT[] array)</Li>
          </Ol>
        </div>

        <div>
          <SubHeading>How Ingredients Get Booleans</SubHeading>
          <Ol>
            <Li><strong>Batch classify:</strong> <Mono>classify_ingredient_dietary_batch</Mono> sends 15 names to AI, gets boolean responses</Li>
            <Li><strong>Encyclopedia view:</strong> AI generates dietary_flags array, converted to booleans</Li>
            <Li>Strict boolean validation (rejects "yes", "maybe" — only true/false accepted)</Li>
          </Ol>
        </div>

        <div>
          <SubHeading>Override Toggles</SubHeading>
          <P>Vegan/vegetarian users can temporarily toggle "Show all content" in the app (e.g., cooking for non-vegetarian guests). Stored in-memory only, resets on logout.</P>
        </div>
      </Section>

      <Section title="Ingredient Lifecycle" icon={Utensils} badge="Documented">
        <div>
          <SubHeading>Two Master Tables</SubHeading>
          <Table
            headers={['Table', 'Purpose', 'Key Fields']}
            rows={[
              ['food_ingredients', 'Master ingredient database', 'name, nutrition per 100g, USDA fdc_id, dietary booleans, description, image'],
              ['food_recipe_ingredients', 'Per-recipe ingredient join', 'recipe_id, ingredient_id (FK, nullable), quantity, unit, preparation'],
            ]}
          />
        </div>

        <div>
          <SubHeading>Post-Save Tasks</SubHeading>
          <P>When a recipe is saved (any path), three async tasks fire:</P>
          <div className="mt-2">
            <Code>{`POST /api/food/recipes  (save recipe + ingredients)
     |
_trigger_recipe_tasks(recipe_id)
     |
+-- 1. update_recipe_embedding       (semantic search vector)
+-- 2. derive_recipe_dietary_flags    (auto-link + classify)
+-- 3. auto_calculate_recipe_nutrition (USDA-based nutrition)`}</Code>
          </div>
        </div>

        <div>
          <SubHeading>Progressive Enrichment</SubHeading>
          <P>A food_ingredient starts as a <strong>stub</strong> and gets enriched over time:</P>
          <div className="mt-2">
            <Table
              headers={['Stage', 'Trigger', 'Data Added']}
              rows={[
                ['1. Stub created', 'derive_recipe_dietary_flags (auto-link)', 'name only'],
                ['2. Dietary booleans', 'Batch classify or encyclopedia view', 'is_vegan, is_vegetarian, etc.'],
                ['3. USDA nutrition', 'Recipe nutrition calc, AIPedia view, or admin Calculate', 'calories_per_100g, protein, carbs, fat, fiber, sugar, usda_fdc_id, nutrition_source=usda'],
                ['4. Full encyclopedia', 'User views ingredient in AIPedia', 'description, image, substitutes, storage_tips, fun_facts, related dishes'],
              ]}
            />
          </div>
        </div>

        <div>
          <SubHeading>2-Tier USDA Caching</SubHeading>
          <Code>{`TIER 1: Persistent (food_ingredients table)
  usda_fdc_id = 123, calories_per_100g = 165, ...
  -> Used immediately on next nutrition calc (no API call)

TIER 2: Session (in-memory dict during calculation)
  {123: {calories: 165, protein_g: 31, ...}}
  -> Avoids duplicate USDA API calls within one recipe calc
  -> Expires when task completes`}</Code>
        </div>
      </Section>

      <Section title="Recipe Embeddings & Semantic Search" icon={Search}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 6: AIPEDIA / ENCYCLOPEDIA                                  */}
      {/* ================================================================ */}
      <PhaseHeader number={6} title="AIPedia / Encyclopedia" />

      <Section title="Ingredient Info" icon={Utensils}>
        <Placeholder />
      </Section>

      <Section title="Dish Info" icon={ChefHat}>
        <Placeholder />
      </Section>

      <Section title="Cuisine Info" icon={Globe}>
        <Placeholder />
      </Section>

      <Section title="Technique & Equipment Info" icon={Zap}>
        <Placeholder />
      </Section>

      <Section title="Pantry Screen (Categories & Browsing)" icon={Layers}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 7: COMMUNITY & SOCIAL                                      */}
      {/* ================================================================ */}
      <PhaseHeader number={7} title="Community & Social" />

      <Section title="Community Feed (Browse, Filter, Sort)" icon={Users}>
        <Placeholder />
      </Section>

      <Section title="Fork / Save Community Recipe" icon={BookOpen}>
        <Placeholder />
      </Section>

      <Section title="Toggle Recipe Public / Private" icon={Globe}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 8: SHOPPING & PANTRY                                       */}
      {/* ================================================================ */}
      <PhaseHeader number={8} title="Shopping & Pantry" />

      <Section title="Shopping Lists CRUD" icon={ShoppingCart}>
        <Placeholder />
      </Section>

      <Section title="Shopping List Items" icon={ShoppingCart}>
        <Placeholder />
      </Section>

      <Section title="Pantry Items CRUD" icon={Utensils}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 9: SUBSCRIPTIONS & BILLING                                 */}
      {/* ================================================================ */}
      <PhaseHeader number={9} title="Subscriptions & Billing" />

      <Section title="Paywall & Tier Limits" icon={CreditCard}>
        <Placeholder />
      </Section>

      <Section title="RevenueCat Purchase Flow" icon={CreditCard}>
        <Placeholder />
      </Section>

      <Section title="Webhook → Tier Update" icon={RefreshCw}>
        <Placeholder />
      </Section>

      <Section title="Consumable Credits (Image Packs)" icon={Image}>
        <Placeholder />
      </Section>

      {/* ================================================================ */}
      {/* PHASE 10: INFRASTRUCTURE                                         */}
      {/* ================================================================ */}
      <PhaseHeader number={10} title="Infrastructure & Operations" />

      <Section title="App Update Check" icon={RefreshCw}>
        <Placeholder />
      </Section>

      <Section title="Firebase Crashlytics" icon={Bug}>
        <Placeholder />
      </Section>

      <Section title="Rate Limiting & Security" icon={Lock}>
        <Placeholder />
      </Section>

      <Section title="AI Provider Resilience (Retry + Fallback)" icon={Server} badge="Documented">
        <div>
          <SubHeading>Claude 529 Retry</SubHeading>
          <P>Both <Mono>call_ai</Mono> (text) and <Mono>_call_claude_with_tools</Mono> (agentic) retry 3x with exponential backoff (1s, 2s) on 529/overloaded errors.</P>
        </div>
        <div>
          <SubHeading>Provider Fallback</SubHeading>
          <P>Genie chat uses <Mono>providers=['claude', 'openai']</Mono> — Claude primary, GPT-4o-mini fallback. OpenAI-compatible providers unified via <Mono>_call_openai_compatible_with_tools</Mono> + registry dict.</P>
        </div>
        <div>
          <SubHeading>Entity Task Dedup</SubHeading>
          <P>AIPedia tasks are deduplicated via Redis. <Mono>_dispatch_or_reuse_task</Mono> checks for in-flight task_id, verifies task is still running via AsyncResult state, returns same id. Prevents duplicate AI/image/USDA work on "Try again" retaps.</P>
        </div>
      </Section>

      <Section title="Operational Queries" icon={Database} badge="Documented">
        <div>
          <SubHeading>Health Checks</SubHeading>
          <Code>{`-- Orphaned recipe ingredients (no master link)
SELECT COUNT(*) FROM finance.food_recipe_ingredients
WHERE ingredient_id IS NULL;

-- Unclassified ingredients (dietary booleans missing)
SELECT COUNT(*) FROM finance.food_ingredients
WHERE dietary_classified IS NULL OR dietary_classified = FALSE;

-- Ingredients without USDA data
SELECT COUNT(*) FROM finance.food_ingredients
WHERE usda_fdc_id IS NULL;

-- Recipes missing nutrition
SELECT COUNT(*) FROM finance.food_recipes
WHERE calories IS NULL;

-- Ingredients with USDA cache (good)
SELECT COUNT(*) FROM finance.food_ingredients
WHERE usda_fdc_id IS NOT NULL AND nutrition_source = 'usda';`}</Code>
        </div>

        <div>
          <SubHeading>Backfill Tasks</SubHeading>
          <Code>{`# Classify dietary booleans for unclassified ingredients
celery call finance_worker.backfill_dietary_classify_only

# Calculate nutrition for recipes missing it
celery call finance_worker.backfill_recipe_nutrition

# Classify dish dietary booleans (encyclopedia)
celery call finance_worker.backfill_dish_dietary`}</Code>
        </div>
      </Section>

      {/* ================================================================ */}
      {/* FAQ                                                              */}
      {/* ================================================================ */}
      <PhaseHeader number="?" title="FAQ" />

      <Section title="Frequently Asked Questions" icon={HelpCircle} badge="Documented">
        <div>
          <SubHeading>How are new ingredients added to the master table?</SubHeading>
          <P>Automatically by <Mono>derive_recipe_dietary_flags</Mono>. When a recipe ingredient has no match in food_ingredients, a <strong>stub</strong> is created (name only). The stub gets progressively enriched: dietary booleans via batch classifier, USDA nutrition via recipe calc, full encyclopedia data when a user views the ingredient page.</P>
        </div>

        <div>
          <SubHeading>What if USDA doesn't have a match?</SubHeading>
          <P>The ingredient keeps AI-estimated nutrition (from the recipe generation prompt). The food_ingredients row has <Mono>nutrition_source = NULL</Mono> or <Mono>'ai'</Mono> instead of <Mono>'usda'</Mono>. Future encyclopedia views will retry the USDA lookup.</P>
        </div>

        <div>
          <SubHeading>What happens to non-English ingredient names?</SubHeading>
          <P>The nutrition calculator detects non-ASCII characters and asks Claude to translate to English before searching USDA (e.g., "tomate" → "tomato"). The translation is used only for the USDA search — the original name is preserved in the database.</P>
        </div>

        <div>
          <SubHeading>Why do some recipes show 0 calories?</SubHeading>
          <P>Either: (a) USDA had no match for the ingredients, (b) the recipe has no ingredients linked, or (c) the <Mono>auto_calculate</Mono> task hasn't run yet. Use the operational queries above to diagnose.</P>
        </div>
      </Section>
    </div>
  );
};

export default DocsPage;
